import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErroCarregamento, { extrairMensagemErro } from '../../components/common/ErroCarregamento';
import PaginacaoUI from '../../components/common/Paginacao';
import StatusBadge from '../../components/common/StatusBadge';
import Pill from '../../components/common/Pill';
import useDebounce from '../../hooks/useDebounce';
import * as eventosApi from '../../api/eventos';
import * as endpointsApi from '../../api/endpoints';
import * as cadastrosApi from '../../api/cadastros';
import { formatDataHora, tempoRelativo } from '../../utils/date';
import { formatBytes } from '../../utils/formato';
import { METODOS } from '../../utils/webhookOpcoes';

const POR_PAGINA = 25;
const INTERVALO_AUTO_S = 10;

export default function Eventos() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filtros, setFiltros] = useState({
    id_endpoint: searchParams.get('id_endpoint') || '',
    id_cliente: searchParams.get('id_cliente') || '',
    sistema: searchParams.get('sistema') || '',
    metodo: '',
    status: '',
    auth_resultado: '',
    duplicado: '',
    busca: '',
  });
  const buscaDebounced = useDebounce(filtros.busca, 500);
  const [pagina, setPagina] = useState(1);
  const [eventos, setEventos] = useState(null);
  const [total, setTotal] = useState(0);
  const [erro, setErro] = useState('');
  const [autoAtualizar, setAutoAtualizar] = useState(false);
  const [endpoints, setEndpoints] = useState([]);
  const [sistemas, setSistemas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const primeiraCarga = useRef(true);

  useEffect(() => {
    // os filtros oferecem o que EXISTE cadastrado — não o catálogo de provedores que o
    // sistema sabe reconhecer, que listava dezenas de nomes nunca usados aqui
    Promise.all([endpointsApi.listar(), eventosApi.sistemas(), cadastrosApi.listar('cliente')])
      .then(([resEndpoints, resSistemas, resClientes]) => {
        setEndpoints(resEndpoints.data);
        // o que existe nos eventos, não o catálogo inteiro de provedores reconhecíveis
        setSistemas(resSistemas.data);
        setClientes(resClientes.data);
      })
      .catch(() => {
        /* os filtros ficam sem as listas auxiliares; a tela principal continua utilizável */
      });
  }, []);

  const params = useMemo(() => {
    const montado = { limit: POR_PAGINA, skip: (pagina - 1) * POR_PAGINA };
    Object.entries({ ...filtros, busca: buscaDebounced }).forEach(([chave, valor]) => {
      if (valor !== '' && valor != null) montado[chave] = valor;
    });
    return montado;
  }, [filtros, buscaDebounced, pagina]);

  const carregar = useCallback(
    async (silencioso = false) => {
      if (!silencioso) setErro('');
      try {
        const resposta = await eventosApi.listar(params);
        setEventos(resposta.data);
        // X-Total-Count vem do backend: a paginação é server-side porque esta tabela cresce sem teto
        setTotal(Number(resposta.headers['x-total-count'] || resposta.data.length));
      } catch (err) {
        if (!silencioso) setErro(extrairMensagemErro(err, 'Não foi possível carregar os eventos.'));
      }
    },
    [params]
  );

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    if (!autoAtualizar) return undefined;
    const timer = setInterval(() => carregar(true), INTERVALO_AUTO_S * 1000);
    return () => clearInterval(timer);
  }, [autoAtualizar, carregar]);

  // mantém o filtro de endpoint na URL: dá para compartilhar o link "eventos desse endpoint"
  useEffect(() => {
    if (primeiraCarga.current) {
      primeiraCarga.current = false;
      return;
    }
    const novos = {};
    if (filtros.id_endpoint) novos.id_endpoint = filtros.id_endpoint;
    if (filtros.id_cliente) novos.id_cliente = filtros.id_cliente;
    if (filtros.sistema) novos.sistema = filtros.sistema;
    setSearchParams(novos, { replace: true });
  }, [filtros.id_endpoint, filtros.id_cliente, filtros.sistema, setSearchParams]);

  const alterarFiltro = (campo, valor) => {
    setPagina(1);
    setFiltros((atual) => ({ ...atual, [campo]: valor }));
  };

  const limparFiltros = () => {
    setPagina(1);
    setFiltros({ id_endpoint: '', id_cliente: '', sistema: '', metodo: '', status: '', auth_resultado: '', duplicado: '', busca: '' });
  };

  const nomeEndpoint = (id) => endpoints.find((e) => e.id_endpoint === id)?.nome || '—';
  const totalPaginas = Math.max(1, Math.ceil(total / POR_PAGINA));
  const temFiltro = Object.values(filtros).some((v) => v !== '');

  if (erro && !eventos) return <ErroCarregamento mensagem={erro} onRetry={() => carregar()} />;

  return (
    <div>
      <PageHeader
        titulo="Eventos"
        subtitulo={`${total} evento(s) ${temFiltro ? 'no filtro atual' : 'recebidos'}`}
        acoes={
          <>
            <label className="wh-btn wh-btn-outline" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={autoAtualizar}
                onChange={(e) => setAutoAtualizar(e.target.checked)}
                style={{ marginRight: 6 }}
              />
              Atualizar a cada {INTERVALO_AUTO_S}s
            </label>
            <button type="button" className="wh-btn wh-btn-outline" onClick={() => carregar()}>
              <i className="bi bi-arrow-clockwise" /> Atualizar
            </button>
          </>
        }
      />

      <div className="card-soft mb-3">
        <div className="card-soft__body">
          <div className="filtros">
            <div style={{ flex: 2, minWidth: 220 }}>
              <label className="wh-label">Buscar no payload</label>
              <input
                className="wh-input"
                placeholder="id do pedido, e-mail, texto do corpo..."
                value={filtros.busca}
                onChange={(e) => alterarFiltro('busca', e.target.value)}
              />
            </div>
            <div>
              <label className="wh-label">Cliente</label>
              <select className="wh-select" value={filtros.id_cliente} onChange={(e) => alterarFiltro('id_cliente', e.target.value)}>
                <option value="">Todos</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="wh-label">Endpoint</label>
              <select className="wh-select" value={filtros.id_endpoint} onChange={(e) => alterarFiltro('id_endpoint', e.target.value)}>
                <option value="">Todos</option>
                {endpoints.map((e) => (
                  <option key={e.id_endpoint} value={e.id_endpoint}>
                    {e.nome}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="wh-label">Sistema</label>
              <select className="wh-select" value={filtros.sistema} onChange={(e) => alterarFiltro('sistema', e.target.value)}>
                <option value="">Todos</option>
                {sistemas.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="wh-label">Método</label>
              <select className="wh-select" value={filtros.metodo} onChange={(e) => alterarFiltro('metodo', e.target.value)}>
                <option value="">Todos</option>
                {METODOS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="wh-label">Status</label>
              <select className="wh-select" value={filtros.status} onChange={(e) => alterarFiltro('status', e.target.value)}>
                <option value="">Todos</option>
                <option value="recebido">Recebido</option>
                <option value="rejeitado">Rejeitado</option>
              </select>
            </div>
            <div>
              <label className="wh-label">Credencial</label>
              <select
                className="wh-select"
                value={filtros.auth_resultado}
                onChange={(e) => alterarFiltro('auth_resultado', e.target.value)}
              >
                <option value="">Todas</option>
                <option value="valido">Válida</option>
                <option value="invalido">Inválida</option>
                <option value="ausente">Ausente</option>
                <option value="nao_aplicavel">Sem validação</option>
              </select>
            </div>
            <div>
              <label className="wh-label">Conteúdo repetido</label>
              <select className="wh-select" value={filtros.duplicado} onChange={(e) => alterarFiltro('duplicado', e.target.value)}>
                <option value="">Todos</option>
                <option value="true">Só os repetidos</option>
                <option value="false">Sem os repetidos</option>
              </select>
            </div>
            {temFiltro && (
              <button type="button" className="wh-btn wh-btn-outline" onClick={limparFiltros}>
                <i className="bi bi-x-lg" /> Limpar
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card-soft">
        {!eventos ? (
          <LoadingSpinner />
        ) : eventos.length === 0 ? (
          <div className="empty-state">
            <i className="bi bi-inbox" />
            <div style={{ fontWeight: 600, color: 'var(--wh-ink)' }}>Nenhum evento encontrado.</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>
              {temFiltro ? 'Tente afrouxar os filtros.' : 'Assim que um provedor chamar /hooks/<slug>, ele aparece aqui.'}
            </div>
          </div>
        ) : (
          <div className="wh-table-wrap">
            <table className="wh-table">
              <thead>
                <tr>
                  <th>Recebido</th>
                  <th>Endpoint</th>
                  <th>Origem</th>
                  <th>Tipo</th>
                  <th>Método</th>
                  <th>Tamanho</th>
                  <th>Credencial</th>
                  <th>Status</th>
                  <th>Sinais</th>
                </tr>
              </thead>
              <tbody>
                {eventos.map((ev) => (
                  <tr key={ev.id_evento} className="is-clicavel" onClick={() => navigate(`/eventos/${ev.id_evento}`)}>
                    <td title={formatDataHora(ev.recebido_em)} style={{ whiteSpace: 'nowrap' }}>
                      {tempoRelativo(ev.recebido_em)}
                    </td>
                    <td>{nomeEndpoint(ev.id_endpoint)}</td>
                    <td>{ev.sistema_detectado || <span style={{ color: 'var(--wh-muted)' }}>não identificado</span>}</td>
                    <td className="wh-mono">{ev.evento_tipo || '—'}</td>
                    <td>
                      <Pill tone="neutral">{ev.metodo}</Pill>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatBytes(ev.tamanho_bytes)}</td>
                    <td>
                      <StatusBadge status={ev.auth_resultado} />
                    </td>
                    <td>
                      <StatusBadge status={ev.status} />
                    </td>
                    <td>
                      <div className="d-flex gap-1 flex-wrap">
                        {ev.duplicado && (
                          <Pill
                            tone="warning"
                            icon="bi-files"
                            title="Corpo idêntico a um evento já recebido neste endpoint dentro da janela de duplicidade. Pode ser reentrega do provedor ou duas notificações distintas com o mesmo conteúdo — o sistema não tem como distinguir quando nada na requisição difere."
                          >
                            conteúdo repetido
                          </Pill>
                        )}
                        {ev.truncado && (
                          <Pill tone="warning" icon="bi-scissors" title="Corpo acima do limite configurado">
                            truncado
                          </Pill>
                        )}
                        {ev.encaminhado !== 'nao' && <StatusBadge status={ev.encaminhado} />}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PaginacaoUI pagina={pagina} totalPaginas={totalPaginas} onChange={setPagina} />
    </div>
  );
}
